import React from 'react';

import {exportSourceFiles} from 'analyses/alignment/AlignmentActions';
import * as analytics from 'analytics/Analytics';
import {AlignmentPolymerType} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {textToDataURI} from 'util/TextToBlobUtil';

export interface AlignmentExportDropdownProps {
  alignmentApiId: string | undefined;
  analysisName: string | undefined;
  attachedFileCount: number;
  getClustalFormatData: () => string;
  getFastaFormatData: () => string;
  loaded: boolean;
  openPDFFormattedPreview: () => void;
  openPDFSnapshotPreview: () => void;
  selectionLength: number;
  sequenceEditURL: string;
}

export default function AlignmentExportDropdown({
  alignmentApiId,
  analysisName,
  attachedFileCount,
  getClustalFormatData,
  getFastaFormatData,
  loaded,
  openPDFFormattedPreview,
  openPDFSnapshotPreview,
  selectionLength,
  sequenceEditURL,
}: AlignmentExportDropdownProps): JSX.Element {
  const alignmentUrl = `${window.location.origin}${sequenceEditURL}?alignment=${alignmentApiId}`;
  const clustalURI = textToDataURI(getClustalFormatData());
  const fastaURI = textToDataURI(getFastaFormatData());
  return (
    <Dropdown
      menu={
        <Menu width={MenuWidth.MEDIUM}>
          <Section>
            <Option href={clustalURI} download={`${analysisName}.clustal`}>
              Clustal
            </Option>
            <Option href={fastaURI} download={`${analysisName}.fasta`}>
              FASTA
            </Option>
          </Section>
          <Section>
            <Option
              onSelect={openPDFSnapshotPreview}
              dataTestAttrs={{component: 'AlignmentExport', element: 'exportPDFSnapshotOption'}}
            >
              PDF - screen capture{selectionLength > 0 && ' (selection)'}
            </Option>
            <Option onSelect={openPDFFormattedPreview}>PDF - formatted</Option>
          </Section>
          {attachedFileCount > 0 && (
            <Section>
              <Option
                onSelect={() => {
                  exportSourceFiles([alignmentApiId!]);
                }}
              >
                Source Files
              </Option>
            </Section>
          )}
          <Section>
            <Option
              onSelect={() => {
                copyToClipboard(alignmentUrl, {
                  showToastOnSuccess: true,
                  onSuccess: () => {
                    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.COPIED_ALIGNMENT_URL, {
                      residueType: AlignmentPolymerType.DNA,
                    });
                  },
                });
              }}
            >
              Copy Link
            </Option>
          </Section>
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        isDropdown={true}
        className="export"
        text="Export"
        disabled={!loaded}
        dataTestAttrs={{component: 'AlignmentExport', element: 'exportButton'}}
      />
    </Dropdown>
  );
}
